import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './MisTurnos.css';

const MisTurnos = () => {
    const { user } = useAuth();
    const [turnos, setTurnos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mensaje, setMensaje] = useState('');

    useEffect(() => {
        const fetchTurnos = async () => {
            try {
                const res = await api.get('/appointments/mis-turnos');
                setTurnos(res.data);
            } catch (err) {
                console.error('Error al cargar turnos:', err);
                setError('Error al cargar tus turnos');
            } finally {
                setLoading(false);
            }
        };
        fetchTurnos();
    }, []);

    const handleEstadoChange = async (id, nuevoEstado) => {
        // Si es cancelación, mostrar advertencia
        if (nuevoEstado === 'cancelado') {
            const esPaciente = user.role === 'paciente';
            let mensajeConfirmacion = '¿Cancelar este turno?';
            
            if (esPaciente) {
                // Verificar si faltan menos de 24hs (opcional, para hacerlo más preciso)
                // const turno = turnos.find(t => t._id === id);
                // const fechaTurno = new Date(turno.fecha);
                // const horasDiferencia = (fechaTurno - new Date()) / 36e5;
                
                mensajeConfirmacion = '⚠️ ATENCIÓN:\n\nSi cancelás el turno con menos de 24 horas de anticipación, perderás la seña abonada.\n\n¿Estás seguro que deseas cancelar?';
            }

            if (!window.confirm(mensajeConfirmacion)) return;
        }

        try {
            await api.patch(`/appointments/${id}/estado`, { estado: nuevoEstado });
            setTurnos(turnos.map(t =>
                t._id === id ? { ...t, estado: nuevoEstado } : t
            ));
            setMensaje(`Turno ${nuevoEstado} correctamente`);
            setTimeout(() => setMensaje(''), 3000);
        } catch (err) {
            console.error('Error al cambiar estado:', err);
            alert(err.response?.data?.message || 'Error al cambiar estado');
        }
    };

    // ============================================
    // LIMPIAR TELÉFONO Y GENERAR LINK WHATSAPP
    // ============================================
    const limpiarTelefono = (tel) => {
        if (!tel) return '';
        return tel.replace(/\D/g, '');
    };

    const generarLinkWhatsApp = (telefono, nombre) => {
        const numeroLimpio = limpiarTelefono(telefono);
        if (!numeroLimpio) return null;

        const numeroFinal = numeroLimpio.startsWith('54')
            ? numeroLimpio
            : `54${numeroLimpio}`;

        const mensaje = encodeURIComponent(
            `Hola ${nombre || ''}, te escribo por tu turno de masaje.`
        );

        return `https://wa.me/${numeroFinal}?text=${mensaje}`;
    };

    // ============================================
    // FORMATEAR PRECIO
    // ============================================
    const formatearPrecio = (valor) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 0,
        }).format(valor || 0);
    };

    // ============================================
    // BADGE DE ESTADO DE PAGO
    // ============================================
    const getPagoBadge = (pagoEstado) => {
        if (!pagoEstado || pagoEstado === 'sin_pago') return null;

        const map = {
            aprobado: { class: 'pago-aprobado', label: '✅ Seña pagada' },
            pendiente: { class: 'pago-pendiente', label: '⏳ Pago pendiente' },
            rechazado: { class: 'pago-rechazado', label: '❌ Pago rechazado' },
            reembolsado: { class: 'pago-reembolsado', label: '↩️ Reembolsado' },
        };

        const info = map[pagoEstado];
        if (!info) return null;

        return (
            <span className={`pago-badge ${info.class}`}>
                {info.label}
            </span>
        );
    };

    if (loading) return <div className="spinner-container"><div className="spinner"></div></div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    const esMasajista = user.role === 'masajista';
    const esPaciente = user.role === 'paciente';

    return (
        <div className="mis-turnos">
            <h2>📅 Mis Turnos</h2>
            {mensaje && <div className="alert alert-success">{mensaje}</div>}
            {turnos.length === 0 ? (
                <p>No tienes turnos programados.</p>
            ) : (
                <div className="turnos-lista">
                    {turnos.map(t => {
                        const persona = esMasajista ? t.paciente : t.masajista;
                        const nombrePersona = persona
                            ? `${persona.nombre || ''} ${persona.apellido || ''}`.trim() || persona.email
                            : 'Sin nombre';
                        const telefonoPersona = persona?.telefono || '';
                        const whatsappLink = generarLinkWhatsApp(telefonoPersona, persona?.nombre);

                        return (
                            <div key={t._id} className={`turno-card estado-${t.estado}`}>
                                <div className="turno-info">
                                    <h5>
                                        {esMasajista ? 'Paciente' : 'Masajista'}: {nombrePersona}
                                    </h5>

                                    {/* Servicio */}
                                    {t.service && (
                                        <p className="turno-servicio">
                                            💆 <strong>{t.service.nombre}</strong>
                                            {t.service.duracion ? ` · ${t.service.duracion} min` : ''}
                                        </p>
                                    )}

                                    <p><strong>Fecha:</strong> {new Date(t.fecha).toLocaleString('es-AR')}</p>

                                    {/* Precio y seña */}
                                    {(t.precio > 0 || t.precioSena > 0) && (
                                        <p className="turno-precio">
                                            {t.precio > 0 && (
                                                <>💰 Total: {formatearPrecio(t.precio)}</>
                                            )}
                                            {t.precio > 0 && t.precioSena > 0 && ' · '}
                                            {t.precioSena > 0 && (
                                                <>📌 Seña: {formatearPrecio(t.precioSena)}</>
                                            )}
                                        </p>
                                    )}

                                    <p><strong>Estado:</strong> <span className={`estado-${t.estado}`}>{t.estado}</span></p>

                                    {/* Estado del pago */}
                                    {t.pagoEstado && t.pagoEstado !== 'sin_pago' && (
                                        <p className="turno-pago-estado">
                                            <strong>Pago:</strong> {getPagoBadge(t.pagoEstado)}
                                        </p>
                                    )}

                                    {/* Teléfono + WhatsApp */}
                                    {telefonoPersona && (
                                        <p className="turno-telefono">
                                            <strong>Teléfono:</strong> {telefonoPersona}
                                            {whatsappLink && (
                                                <a
                                                    href={whatsappLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-whatsapp"
                                                    title="Abrir en WhatsApp"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 24 24"
                                                        width="18"
                                                        height="18"
                                                        fill="currentColor"
                                                    >
                                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                    </svg>
                                                </a>
                                            )}
                                        </p>
                                    )}

                                    {t.notas && <p><strong>Notas:</strong> {t.notas}</p>}
                                </div>

                                {esMasajista && (
                                    <div className="turno-acciones">
                                        <button
                                            className="btn btn-sm btn-success"
                                            onClick={() => handleEstadoChange(t._id, 'confirmado')}
                                            disabled={t.estado !== 'pendiente'}
                                        >
                                            Confirmar
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => handleEstadoChange(t._id, 'cancelado')}
                                            disabled={t.estado === 'finalizado' || t.estado === 'cancelado'}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => handleEstadoChange(t._id, 'finalizado')}
                                            disabled={t.estado !== 'confirmado'}
                                        >
                                            Finalizar
                                        </button>
                                    </div>
                                )}

                                {esPaciente && (t.estado === 'pendiente' || t.estado === 'confirmado') && (
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleEstadoChange(t._id, 'cancelado')}
                                    >
                                        Cancelar turno
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MisTurnos;